import { stableStringify } from '@cotera/utilities';
import type { DatasetColumnValue, DatasetRow } from './dataset';

/** Opaque per-row uuid stamped on manual dataset rows. */
export const COTERA_ROW_ID_FIELD = '__cotera_row_id';
/** 1-based data row number stamped on CSV artifacts at parse time. */
export const COTERA_CSV_ROW_NUMBER_FIELD = '__cotera_csv_row_number';
export const COTERA_COMPACTION_ORDER_FIELD = '__cotera_compaction_order';
export const COTERA_PREVIEW_ORDER_FIELD = '__cotera_preview_order';

const INTERNAL_COLUMNS: ReadonlySet<string> = new Set([
  COTERA_ROW_ID_FIELD,
  COTERA_CSV_ROW_NUMBER_FIELD,
  COTERA_COMPACTION_ORDER_FIELD,
  COTERA_PREVIEW_ORDER_FIELD,
]);

/** Bookkeeping columns the product owns; callers may never name one. */
export const isInternalDatasetColumn = (columnName: string): boolean =>
  INTERNAL_COLUMNS.has(columnName);

/**
 * How a dataset identifies a row.
 *
 * `internal: true` means the product owns the column (an opaque uuid or a row
 * number) and the user never sees it. `internal: false` means the user declared
 * business columns, which is the only option for kinds whose storage we do not
 * own (warehouse, sheets) and the correct choice for any dataset that gets
 * re-imported — a positional row number does not survive an upstream insert.
 *
 * A dataset may *declare* the internal column, and a CSV one does from its first
 * import: the key it is already being identified by is written down rather than
 * left implied, so the settings dialog has something to show. That is a change
 * in what is recorded and not in what a row is, which is why it still resolves
 * to `internal: true` — everything asking "does this survive a re-import" has to
 * keep answering no.
 */
export type DatasetRowKeySpec = {
  readonly columns: readonly string[];
  readonly internal: boolean;
};

/**
 * Whether a declared key names the product's own row id rather than the user's
 * data.
 *
 * The distinction every warning depends on: a dataset with this declaration is
 * keyed, but by a number the next upload reassigns, so it is exactly as fragile
 * as one that declared nothing at all.
 */
export const isInternalRowKeyDeclaration = (
  rowKeyColumns: readonly string[] | null | undefined
): boolean =>
  rowKeyColumns !== null &&
  rowKeyColumns !== undefined &&
  rowKeyColumns.length > 0 &&
  rowKeyColumns.every((column) => isInternalDatasetColumn(column));

/**
 * Whether two resolved specs identify a row the same way.
 *
 * Order matters: a compound key encodes its columns positionally. Compares the
 * resolved specs rather than the stored arrays on purpose — declaring the
 * internal column a dataset already fell back to reads as a change to the
 * column and as no change at all to identity, and it is identity that decides
 * whether observed trigger state has to be carried across.
 */
export const sameDatasetRowKeySpec = (
  a: DatasetRowKeySpec | null,
  b: DatasetRowKeySpec | null
): boolean => {
  if (a === null || b === null) {
    return a === b;
  }
  return (
    a.internal === b.internal &&
    a.columns.length === b.columns.length &&
    a.columns.every((column, index) => column === b.columns[index])
  );
};

type NamedColumn = { readonly name: string };

/**
 * Resolves the internal row-id column from an artifact schema. Returns null for
 * legacy CSV artifacts written before row numbers were stamped, which is what
 * makes a dataset read-only in the grid.
 */
export const datasetRowIdColumn = (
  columns: readonly NamedColumn[]
): string | null => {
  if (columns.some((column) => column.name === COTERA_ROW_ID_FIELD)) {
    return COTERA_ROW_ID_FIELD;
  }
  if (columns.some((column) => column.name === COTERA_CSV_ROW_NUMBER_FIELD)) {
    return COTERA_CSV_ROW_NUMBER_FIELD;
  }
  return null;
};

/**
 * Declared business columns win over the internal column. Returns null when
 * neither is available, meaning rows in this dataset cannot be identified at
 * all.
 *
 * A declaration naming only internal columns resolves through the artifact
 * rather than being taken at its word, so a dataset that declared the CSV row
 * number and was later rewritten with a different row-id strategy still names
 * the column its rows actually carry.
 */
export const resolveDatasetRowKeySpec = (params: {
  readonly columns: readonly NamedColumn[];
  readonly rowKeyColumns?: readonly string[] | null;
}): DatasetRowKeySpec | null => {
  const declared = params.rowKeyColumns ?? [];
  if (declared.length > 0 && !isInternalRowKeyDeclaration(declared)) {
    return { columns: declared, internal: false };
  }
  const internal = datasetRowIdColumn(params.columns);
  if (internal === null) {
    return null;
  }
  return { columns: [internal], internal: true };
};

/**
 * The internal row id carried by a row, as a string, or null when the row has
 * none (legacy artifacts predate both stamps).
 *
 * Distinct from {@link resolveRowKey}, which answers "what identifies this row"
 * and prefers declared business columns. This answers "what does the artifact
 * call this row", which is what a client-side join keys on — a business key can
 * be multi-column and canonical-JSON encoded, and nothing outside this module
 * should try to reproduce that encoding.
 */
export const datasetInternalRowId = (
  row: Readonly<Record<string, unknown>>
): string | null => {
  const value = row[COTERA_ROW_ID_FIELD] ?? row[COTERA_CSV_ROW_NUMBER_FIELD];
  if (value === null || value === undefined) {
    return null;
  }
  return String(value);
};

/**
 * The values a trigger reports or compares, taken from a row.
 *
 * Internal columns are never included — a user never asked to watch a row id —
 * and dates become ISO strings so a value read back out of storage compares equal
 * to one taken straight off a row.
 *
 * `trackFields: null` means every non-internal column. That is the form used for
 * the `before`/`after` a dispatch reports, which deliberately carries the whole
 * row rather than the watched subset: narrowing one side and not the other makes
 * every unwatched column look changed when the two are diffed.
 */
export const datasetTrackedValues = (
  // Loose on input so this works both on a freshly read row and on one that has
  // been through JSON — a dispatch projects it from a row the saga journaled.
  row: Readonly<Record<string, unknown>>,
  trackFields: readonly string[] | null
): Record<string, unknown> => {
  const values: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(row)) {
    if (isInternalDatasetColumn(name)) {
      continue;
    }
    if (trackFields !== null && !trackFields.includes(name)) {
      continue;
    }
    values[name] = value instanceof Date ? value.toISOString() : value;
  }
  return values;
};

const normalizeKeyValue = (value: DatasetColumnValue): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return String(value);
};

/**
 * Builds the stable identity string for a row.
 *
 * Single-column specs return the raw value so that a `__cotera_row_id` key stays
 * a plain uuid — keys show up in run history and support tables, and readable
 * beats hashed there. Multi-column specs use canonical JSON so that column order
 * and value types cannot collide two different rows onto one key.
 *
 * Returns null when any key column is absent or null: a row we cannot identify
 * must not be silently folded onto some other row's key.
 */
export const resolveRowKey = (
  spec: DatasetRowKeySpec,
  row: DatasetRow
): string | null => {
  const values: string[] = [];
  for (const column of spec.columns) {
    const normalized = normalizeKeyValue(row[column] ?? null);
    if (normalized === null) {
      return null;
    }
    values.push(normalized);
  }
  if (values.length === 0) {
    return null;
  }
  if (values.length === 1) {
    return values[0] ?? null;
  }
  return stableStringify(values);
};
